<template>
  <div class="container">
    <aside class="layout_shell layout_left_shell">
      <div class="left">
        <operations />
      </div>
    </aside>

    <section class="layout_shell layout_center_shell">
      <div class="center">
        <header class="center_header">
          <navigation />
        </header>

        <div class="main">
          <div class="virtual_keyboard">
            <device_wrapper />
          </div>

          <div class="control_pannel" :class="{ 'is-mgx-panel': currentActionType === OperateType.mgx }">
            <div class="control_pannel_viewport">
              <div class="control_pannel_content">
                <component :is="currentControlPanel" v-if="currentControlPanel" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <aside class="layout_shell layout_right_shell">
      <right_sidebar />
    </aside>
  </div>
</template>

<script setup lang="ts">
import { operate, OperateType } from '@/service/driver/operate'
import { computed, type Component } from 'vue'
import Navigation from '../navigation/navigation.vue'
import Operations from '../operations/operations.vue'
import Advanced_set from '../operation_advanced/advanced_set.vue'
import Basic from '../operation_basic/basic.vue'
import Mgx_set from '../mgx_set/mgx_set.vue'
import Light from '../light/light.vue'
import Device_wrapper from '../device/device_wrapper.vue'
import Operation_action from '../operation_action/operation_action.vue'
import Right_sidebar from './right_sidebar.vue'

const currentActionType = computed(() => operate.getActionType().value)

const controlPanelMap: Partial<Record<OperateType, Component>> = {
  [OperateType.base]: Basic,
  [OperateType.advanced]: Advanced_set,
  [OperateType.mgx]: Mgx_set,
  [OperateType.light]: Light,
  [OperateType.action]: Operation_action,
}

const currentControlPanel = computed<Component | undefined>(() => {
  return controlPanelMap[currentActionType.value]
})
</script>

<style lang="less" scoped>
.main {
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-rows: minmax(clamp(220px, 30vh, 360px), 1fr) auto;
  align-items: stretch;
  padding-bottom: 12px;
  gap: 4px;
  overflow: hidden;

  .virtual_keyboard {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 0;
    min-height: 0;
    overflow: hidden;

    :deep(.device_container) {
      justify-content: center;
      min-height: 0;
      height: 100%;
    }

    :deep(.device) {
      max-width: 100%;
    }
  }

  .control_pannel {
    padding: 0 20px;
    transition:
      transform 0.4s cubic-bezier(0.23, 1, 0.32, 1),
      width 0.4s cubic-bezier(0.23, 1, 0.32, 1),
      margin 0.4s cubic-bezier(0.23, 1, 0.32, 1);
    // transition: all 0.4s cubic-bezier(0.23, 1, 0.32, 1);
    width: 100%;
    min-height: 0;
    display: flex;
    flex-direction: column;
    flex: 0 0 auto;
    overflow: hidden;

    .control_pannel_viewport {
      flex: 0 0 auto;
      min-height: 0;
      display: flex;
      flex-direction: column;
      overflow-x: hidden;
      overflow-y: auto;
    }

    .control_pannel_content {
      flex: 0 0 auto;
      min-height: 0;
      display: flex;
      flex-direction: column;
    }

    :deep(.basic-view),
    :deep(.advanced_set_box),
    :deep(.light-container),
    :deep(.operation_action_panel),
    :deep(.main_settings_card) {
      min-height: 0;
    }

    &.is-mgx-panel {
      overflow: visible;

      .control_pannel_viewport,
      .control_pannel_content {
        overflow: visible;
      }
    }
  }
}

.container {
  display: grid;
  grid-template-columns: var(--sidebar-width-v5) minmax(0, 1fr) var(--sidebar-right-width-v5);
  gap: 0;
  position: relative;
  z-index: 0;
  isolation: isolate;
  flex: 1;
  height: 100%;
  min-height: 0;
  border: none;
  border-radius: 0;
  background: transparent;
  // backdrop-filter: blur(24px); // TODO:
  overflow: hidden;

  .layout_shell {
    min-height: 0;
  }

  .layout_left_shell,
  .layout_right_shell {
    display: flex;
    backdrop-filter: none;
    border: none;
    border-radius: 0;
    box-shadow: none;
    overflow: hidden;
  }

  .layout_left_shell {
    border-right: var(--panel-line-soft);
  }

  .layout_right_shell {
    border-left: var(--panel-line-soft);
  }

  .layout_center_shell {
    display: flex;
    min-width: 0;
    padding: 0;
    position: relative;
    z-index: 2;
    overflow: visible;
  }

  .left {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    padding: 0;
    gap: 0;
    flex-shrink: 0;
    overflow: hidden;
    transition:
      background-color 0.4s,
      border-color 0.4s,
      transform 0.4s;
    -webkit-backface-visibility: hidden;
    backface-visibility: hidden;
    transform: translateZ(0);
  }

  .center {
    flex: 1;
    flex-grow: 1;
    flex-shrink: 1;
    display: flex;
    flex-direction: column;
    padding: 0;
    min-height: 0;
    min-width: 0;
    position: relative;
    z-index: 2;
    border-radius: 0;
    backdrop-filter: none;
    border: none;
    box-shadow: none;
    transition:
      width 0.2s,
      opacity 0.1s;

    .center_header {
      flex-shrink: 0;
      min-height: 84px;
      border-bottom: var(--panel-line-strong);
    }
  }
}

/* =========================================
   响应式缩放等级 (Responsive Scaling)
   按照屏幕高度进行缩放适配
   您可以在下方的 scale() 中填入具体的缩放比例 
========================================= */

/* 适配 2K 屏幕 (2560x1440) 或超大屏开启缩放 (可用高度通常在 1200px - 1440px) */
@media screen and (max-height: 2440px) {
  .main .virtual_keyboard {
    transform: scale(1.5);
    transform-origin: center center;
    :deep(.mouse_view) {
      transform: scale(0.56);
      transform-origin: center center;
    }
  }
  .main .control_pannel {
    transform: scale(1.2);
    transform-origin: bottom center;
    width: 82.9%; /* 抵消 1.1 倍拉伸带来的横向溢出 */
    margin: 0 auto;
  }
}

/* 适配高度较低的大屏或中等笔记本 (例如 1080p 屏幕，高度减去任务栏通常为 1000px 以内) */
@media screen and (max-height: 1180px) {
  .main .virtual_keyboard {
    transform: scale(1.1);
    transform-origin: center center;
    :deep(.mouse_view) {
      transform: scale(0.56);
      transform-origin: center center;
    }
  }
  .main .control_pannel {
    transform: scale(1);
    transform-origin: top center;
    width: 100%;
  }
}

/* 适配常见笔记本屏幕 (例如 15.6寸 1080p 开启了 125% 缩放，可用高度通常在 800px 左右) */
@media screen and (max-height: 905px) {
  .main .virtual_keyboard {
    transform: scale(1);
    transform-origin: center center;
    :deep(.mouse_view) {
      transform: scale(0.56);
      transform-origin: center center;
    }
  }
  .main .control_pannel {
    transform: scale(1);
    transform-origin: top center;
    width: 100%;
  }
}

@media screen and (max-height: 825px) {
  .main .virtual_keyboard {
    transform: scale(0.85);
    transform-origin: center center;
    :deep(.mouse_view) {
      transform: scale(0.56);
      transform-origin: center center;
    }
  }
  .main .control_pannel {
    transform: scale(1);
    transform-origin: top center;
    width: 100%;
  }
}
</style>
